import { useMemo, useState } from 'react';
import {
  Button as AntButton,
  Card,
  Col,
  Divider,
  Flex,
  Modal,
  Row,
  Select as AntSelect,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useQuery } from '@tanstack/react-query';

import Button from '../../../custom/button/button';
import Input from '../../../custom/input/input';
import {
  ScholarshipApplication,
  useGetAllScholarshipApplicationQuery,
} from '../../../hooks/api/scholarship/useGetAllScholarshipApplicationQuery';
import { useGetApplicantScholarshipApplicationQuery } from '../../../hooks/api/scholarship/useGetApplicantScholarshipApplicationQuery';
import { useGetScholarshipPercentageConfigQuery } from '../../../hooks/api/scholarship/useGetScholarshipPercentageConfigQuery';
import {
  ScholarshipStageActionPayload,
  useScholarshipStageActionsMutation,
} from '../../../hooks/api/scholarship/useScholarshipStageActionsMutation';
import {
  ScholarshipPendingRecord,
  useGetScholarshipPendingByStageQuery,
} from '../../../hooks/api/scholarship/useGetScholarshipPendingByStageQuery';
import { getScholarshipStages } from '../scholarshipApproval/request';
import { formatFullDate, formattedTime } from '../../../utils/formatDate';
import userStyles from '../../userManagement/styles.module.scss';

const { Text } = Typography;

enum ScholarshipApplicationStatus {
  Pending = 0,
  UnderReview = 1,
  Approved = 2,
  Rejected = 3,
  Waitlisted = 4,
  AcceptanceUploaded = 5,
}

const statusColorMap: Record<number, string> = {
  [ScholarshipApplicationStatus.Pending]: 'default',
  [ScholarshipApplicationStatus.UnderReview]: 'processing',
  [ScholarshipApplicationStatus.Approved]: 'success',
  [ScholarshipApplicationStatus.Rejected]: 'error',
  [ScholarshipApplicationStatus.Waitlisted]: 'warning',
  [ScholarshipApplicationStatus.AcceptanceUploaded]: 'purple',
};

type ReviewFormValues = {
  scholarshipPercent: number | '';
  notes: string;
};

const reviewValidationSchema = Yup.object().shape({
  scholarshipPercent: Yup.number().required('Scholarship percentage is required'),
  notes: Yup.string().trim().required('Notes are required'),
});

const formatDate = (date?: string) => {
  if (!date) return 'N/A';
  return [formatFullDate(date), formattedTime(date)].filter(Boolean).join(' ');
};

const InfoItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Col xs={24} sm={12} lg={8} className={userStyles.infoItem}>
    <Text className={userStyles.infoLabel}>{label}</Text>
    <div className={userStyles.infoValue}>{value || 'N/A'}</div>
  </Col>
);

interface ReviewActionsProps {
  scholarshipAppId: number;
  stageOrder: number;
  onApprove: (id: number, payload: ScholarshipStageActionPayload) => Promise<void>;
  onReject: (id: number, payload: ScholarshipStageActionPayload) => Promise<void>;
  onWaitlist: (id: number, payload: ScholarshipStageActionPayload) => Promise<void>;
  isApproving: boolean;
  isRejecting: boolean;
  isWaitlisting: boolean;
}

const ReviewActions = ({
  scholarshipAppId,
  stageOrder,
  onApprove,
  onReject,
  onWaitlist,
  isApproving,
  isRejecting,
  isWaitlisting,
}: ReviewActionsProps) => {
  const { values, validateForm, setTouched } = useFormikContext<ReviewFormValues>();
  const isAnyLoading = isApproving || isRejecting || isWaitlisting;

  const buildPayload = (): ScholarshipStageActionPayload => ({
    scholarshipApplicationId: scholarshipAppId,
    stageOrder,
    notes: values.notes,
    scholarshipPercent: Number(values.scholarshipPercent),
  });

  const handleAction = async (
    action: (id: number, payload: ScholarshipStageActionPayload) => Promise<void>,
  ) => {
    await setTouched({ scholarshipPercent: true, notes: true });
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      await action(scholarshipAppId, buildPayload());
    }
  };

  return (
    <Flex gap={8} wrap="wrap">
      <AntButton
        type="primary"
        style={{ background: '#12b76a', borderColor: '#12b76a' }}
        loading={isApproving}
        disabled={isAnyLoading}
        onClick={() => handleAction(onApprove)}
      >
        Approve
      </AntButton>
      <AntButton
        danger
        type="primary"
        loading={isRejecting}
        disabled={isAnyLoading}
        onClick={() => handleAction(onReject)}
      >
        Reject
      </AntButton>
      <AntButton
        type="primary"
        loading={isWaitlisting}
        disabled={isAnyLoading}
        onClick={() => handleAction(onWaitlist)}
      >
        Waitlist
      </AntButton>
    </Flex>
  );
};

interface PendingByStageTableProps {
  stageOrder: number;
  onView: (record: ScholarshipPendingRecord) => void;
}

const PendingByStageTable = ({ stageOrder, onView }: PendingByStageTableProps) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { pendingRecords, totalRecords, isLoading } = useGetScholarshipPendingByStageQuery(
    stageOrder,
    page,
    pageSize,
  );

  const columns: ColumnsType<ScholarshipPendingRecord> = [
    { key: 'applicationNumber', title: 'Application No', dataIndex: 'applicationNumber' },
    {
      key: 'applicantName',
      title: 'Applicant Name',
      render: (_, r) => [r.firstName, r.middleName, r.lastName].filter(Boolean).join(' '),
    },
    { key: 'programName', title: 'Program', dataIndex: 'programName' },
    { key: 'stageName', title: 'Stage', dataIndex: 'stageName' },
    {
      key: 'actedBy',
      title: 'Forwarded By',
      dataIndex: 'actedBy',
      render: (v) => v || 'N/A',
    },
    {
      key: 'submittedAt',
      title: 'Submitted At',
      dataIndex: 'submittedAt',
      render: (date) => formatDate(date),
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => (
        <AntButton type="text" onClick={() => onView(record)}>
          View
        </AntButton>
      ),
    },
  ];

  return (
    <Table
      dataSource={pendingRecords}
      columns={columns}
      rowKey={(r) => r.scholarshipApplicationId}
      loading={isLoading}
      scroll={{ x: true }}
      locale={{ emptyText: 'No pending applications for this stage' }}
      pagination={{
        position: ['bottomCenter'],
        current: page,
        total: totalRecords,
        pageSize,
        onChange: (p, ps) => {
          setPage(p);
          setPageSize(ps);
        },
      }}
    />
  );
};

interface SelectedContext {
  applicantId: string;
  scholarshipApplicationId: number;
  currentStageOrder: number;
}

const ScholarshipApplicationPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedContext, setSelectedContext] = useState<SelectedContext | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const { data: stagesData } = useQuery({
    queryKey: ['get-scholarship-stages'],
    queryFn: getScholarshipStages,
  });
  const stages = stagesData?.data ?? [];

  const {
    scholarshipApplicationsData,
    totalRecords,
    isScholarshipApplicationsLoading,
    isScholarshipApplicationsError,
    scholarshipApplicationsErrorMessage,
  } = useGetAllScholarshipApplicationQuery(currentPage, pageSize);

  const { percentageConfigData } = useGetScholarshipPercentageConfigQuery();

  const percentageOptions = useMemo(
    () =>
      (percentageConfigData?.generatedOptions ?? []).map((v) => ({ label: `${v}%`, value: v })),
    [percentageConfigData],
  );

  const {
    applicantScholarshipApplicationData,
    isApplicantScholarshipApplicationLoading,
    isApplicantScholarshipApplicationError,
    applicantScholarshipApplicationErrorMessage,
  } = useGetApplicantScholarshipApplicationQuery(selectedContext?.applicantId);

  const handleCloseModal = () => {
    setSelectedContext(null);
    setShowReviewForm(false);
  };

  const { approve, reject, waitlist, isApproving, isRejecting, isWaitlisting } =
    useScholarshipStageActionsMutation(handleCloseModal);

  const scholarshipAppId =
    selectedContext?.scholarshipApplicationId ?? applicantScholarshipApplicationData?.id ?? 0;

  const effectiveStageOrder =
    selectedContext?.currentStageOrder ||
    applicantScholarshipApplicationData?.currentStageOrder ||
    0;

  const firstVisibleRecord = totalRecords ? (currentPage - 1) * pageSize + 1 : 0;
  const lastVisibleRecord = totalRecords
    ? Math.min(firstVisibleRecord + scholarshipApplicationsData.length - 1, totalRecords)
    : 0;

  const allColumns: ColumnsType<ScholarshipApplication> = [
    { key: 'applicationNumber', title: 'Application No', dataIndex: 'applicationNumber' },
    { key: 'applicantName', title: 'Applicant Name', dataIndex: 'applicantName' },
    {
      key: 'originalFileName',
      title: 'File Name',
      dataIndex: 'originalFileName',
      render: (v) => v || 'N/A',
    },
    {
      key: 'scholarshipPercent',
      title: 'Scholarship %',
      dataIndex: 'scholarshipPercent',
      render: (v: number | null) => (v !== null && v !== undefined ? `${v}%` : '—'),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      render: (status, record) => (
        <Tag color={statusColorMap[status] ?? 'default'}>{record.statusLabel || 'N/A'}</Tag>
      ),
    },
    {
      key: 'submittedAt',
      title: 'Submitted At',
      dataIndex: 'submittedAt',
      render: (date) => formatDate(date),
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => (
        <AntButton
          type="text"
          onClick={() =>
            setSelectedContext({
              applicantId: record.applicantId,
              scholarshipApplicationId: record.id,
              currentStageOrder: 0,
            })
          }
        >
          View
        </AntButton>
      ),
    },
  ];

  const handleViewPending = (record: ScholarshipPendingRecord) => {
    setSelectedContext({
      applicantId: record.applicantId,
      scholarshipApplicationId: record.scholarshipApplicationId,
      currentStageOrder: record.currentStageOrder,
    });
  };

  const tabItems = [
    {
      key: 'all',
      label: 'All Applications',
      children: (
        <Card bordered={false}>
          <p>
            Showing {firstVisibleRecord}–{lastVisibleRecord} of {totalRecords}
          </p>
          <Table
            dataSource={scholarshipApplicationsData}
            columns={allColumns}
            rowKey={(r) => r.id}
            loading={isScholarshipApplicationsLoading}
            scroll={{ x: true }}
            pagination={{
              position: ['bottomCenter'],
              current: currentPage,
              total: totalRecords,
              pageSize,
              onChange: (p, ps) => {
                setCurrentPage(p);
                setPageSize(ps);
              },
            }}
          />
        </Card>
      ),
    },
    ...stages.map((stage) => ({
      key: String(stage.stageOrder),
      label: stage.stageName,
      children: (
        <Card bordered={false}>
          <PendingByStageTable stageOrder={stage.stageOrder} onView={handleViewPending} />
        </Card>
      ),
    })),
  ];

  if (isScholarshipApplicationsError) {
    return <div>Error: {scholarshipApplicationsErrorMessage}</div>;
  }

  return (
    <main>
      <section className="space-between">
        <h3>Scholarship Applications</h3>
      </section>

      <Tabs items={tabItems} />

      <Modal
        open={Boolean(selectedContext)}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
        width={860}
        title="Scholarship Application"
      >
        {isApplicantScholarshipApplicationLoading ? (
          <Spin />
        ) : isApplicantScholarshipApplicationError ? (
          <Text type="danger">{applicantScholarshipApplicationErrorMessage}</Text>
        ) : (
          applicantScholarshipApplicationData && (
            <div className={userStyles.verifyForm}>
              <Row gutter={[16, 12]} className={userStyles.infoGrid}>
                <InfoItem
                  label="Institution"
                  value={applicantScholarshipApplicationData.institutionShortName}
                />
                <InfoItem
                  label="Application No"
                  value={applicantScholarshipApplicationData.applicationNumber}
                />
                <InfoItem
                  label="Applicant Name"
                  value={applicantScholarshipApplicationData.applicantName}
                />
                <InfoItem
                  label="Current Stage"
                  value={applicantScholarshipApplicationData.stageName}
                />
                <InfoItem
                  label="Status"
                  value={
                    <Tag
                      color={
                        statusColorMap[applicantScholarshipApplicationData.status] ?? 'default'
                      }
                    >
                      {applicantScholarshipApplicationData.statusLabel}
                    </Tag>
                  }
                />
                <InfoItem
                  label="Original File"
                  value={applicantScholarshipApplicationData.originalFileName}
                />
                <InfoItem
                  label="Submitted File"
                  value={applicantScholarshipApplicationData.submittedFileName}
                />
                <InfoItem
                  label="Admin Remarks"
                  value={applicantScholarshipApplicationData.adminRemarks}
                />
                <InfoItem
                  label="Reviewed By"
                  value={applicantScholarshipApplicationData.reviewedBy}
                />
                <InfoItem
                  label="Reviewed At"
                  value={formatDate(applicantScholarshipApplicationData.reviewedAt || '')}
                />
                <InfoItem
                  label="Submitted At"
                  value={formatDate(applicantScholarshipApplicationData.submittedAt)}
                />
                <Col span={24} className={userStyles.infoItem}>
                  <Text className={userStyles.infoLabel}>Application File</Text>
                  {applicantScholarshipApplicationData.filePath?.startsWith('http') ? (
                    <a
                      className={userStyles.link}
                      href={applicantScholarshipApplicationData.filePath}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View submitted file
                    </a>
                  ) : (
                    <div className={userStyles.infoValue}>
                      {applicantScholarshipApplicationData.filePath || 'N/A'}
                    </div>
                  )}
                </Col>
              </Row>

              <Flex justify="flex-end">
                <Button
                  type="button"
                  text={showReviewForm ? 'Hide Review' : 'Review Application'}
                  onClick={() => setShowReviewForm((v) => !v)}
                />
              </Flex>

              {showReviewForm && (
                <>
                  <Divider />
                  <Formik<ReviewFormValues>
                    initialValues={{
                      scholarshipPercent:
                        applicantScholarshipApplicationData.scholarshipPercent ?? '',
                      notes: applicantScholarshipApplicationData.adminRemarks || '',
                    }}
                    validationSchema={reviewValidationSchema}
                    onSubmit={() => {}}
                  >
                    {({ values, setFieldValue, errors, touched }) => (
                      <Form className={userStyles.verifyForm}>
                        <div>
                          <label
                            style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#344054' }}
                          >
                            Scholarship Percentage
                          </label>
                          <AntSelect
                            placeholder="Select percentage"
                            size="large"
                            style={{ width: '100%' }}
                            value={values.scholarshipPercent !== '' ? values.scholarshipPercent : undefined}
                            onChange={(val) => setFieldValue('scholarshipPercent', val)}
                            options={percentageOptions}
                            loading={!percentageConfigData}
                          />
                          {touched.scholarshipPercent && errors.scholarshipPercent && (
                            <div style={{ color: '#d92d20', fontSize: '0.75rem', marginTop: 4 }}>
                              {errors.scholarshipPercent as string}
                            </div>
                          )}
                        </div>

                        <Input
                          name="notes"
                          label="Notes"
                          placeholder="Enter review notes"
                          type="textarea"
                        />

                        <Flex
                          justify="flex-end"
                          align="center"
                          gap="8px"
                          wrap="wrap"
                          className={userStyles.verifyActions}
                        >
                          <AntButton onClick={() => setShowReviewForm(false)}>Cancel</AntButton>
                          <ReviewActions
                            scholarshipAppId={scholarshipAppId}
                            stageOrder={effectiveStageOrder}
                            onApprove={approve}
                            onReject={reject}
                            onWaitlist={waitlist}
                            isApproving={isApproving}
                            isRejecting={isRejecting}
                            isWaitlisting={isWaitlisting}
                          />
                        </Flex>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
            </div>
          )
        )}
      </Modal>
    </main>
  );
};

export default ScholarshipApplicationPage;
